import { logClientAuditEvent } from '../project/services/auditTrailApi';
import { ensureAuditSession } from '../audit/auditSession';

const ACTIVITY_MODULE = 'ACTIVITY';

let lastTabKey = '';

function debounceKey(key: string, store: { current: string }, windowMs = 500): boolean {
  if (store.current === key) return false;
  store.current = key;
  window.setTimeout(() => {
    if (store.current === key) store.current = '';
  }, windowMs);
  return true;
}

const tabStore = { current: '' };
const searchStore = { current: '' };
const filterStore = { current: '' };

export type ActivityProjectContext = {
  projectId?: number | string | null;
  projectName?: string | null;
};

function normalizeActivityProjectId(projectId?: number | string | null): number | undefined {
  if (projectId == null) return undefined;
  if (typeof projectId === 'number') return Number.isFinite(projectId) ? projectId : undefined;
  const parsed = Number(projectId);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function formatActivityProjectLabel(ctx?: ActivityProjectContext): string {
  const name = ctx?.projectName?.trim();
  if (name) return ` in ${name}`;
  const id = normalizeActivityProjectId(ctx?.projectId);
  if (id != null) return ` in project ${id}`;
  return '';
}

function resolveActivityProjectContext(meta?: Record<string, unknown>): {
  projectId?: number;
  projectName?: string;
  meta: Record<string, unknown>;
} {
  if (!meta) return { meta: {} };
  const { projectId: rawId, projectName: rawName, ...rest } = meta;
  const projectId = normalizeActivityProjectId(rawId as number | string | null | undefined);
  const projectName = typeof rawName === 'string' && rawName.trim() ? rawName.trim() : undefined;
  return { projectId, projectName, meta: rest };
}

export function logActivityTabView(tab: string, projectId?: number | string, projectName?: string | null): void {
  ensureAuditSession();
  const key = `${projectId ?? 'na'}:${tab}`;
  if (key === lastTabKey) return;
  lastTabKey = key;

  const normalizedId = normalizeActivityProjectId(projectId);
  const projectLabel = formatActivityProjectLabel({ projectId: normalizedId, projectName });

  logClientAuditEvent({
    eventType: 'ACTIVITY_TAB_VIEW',
    module: ACTIVITY_MODULE,
    entityType: 'UI',
    entityId: `tab-${tab}`,
    entityName: tab,
    projectId: normalizedId,
    projectName: projectName?.trim() || undefined,
    summary: `Opened ${tab} tab${projectLabel}.`,
    displayTitle: tab,
    actionDetails: {
      tab,
      projectId: normalizedId ?? null,
      projectName: projectName?.trim() || null,
      route: typeof window !== 'undefined' ? window.location.pathname : null,
    },
  });
}

export function logActivityPageView(page: string, meta?: Record<string, unknown>): void {
  ensureAuditSession();
  const { projectId, projectName, meta: rest } = resolveActivityProjectContext(meta);
  const projectLabel = formatActivityProjectLabel({ projectId, projectName });

  logClientAuditEvent({
    eventType: 'ACTIVITY_PAGE_VIEW',
    module: ACTIVITY_MODULE,
    entityType: 'UI',
    entityId: `page-${page}`,
    entityName: page,
    projectId,
    projectName,
    summary: `Opened ${page}${projectLabel}.`,
    displayTitle: page,
    actionDetails: { page, projectId: projectId ?? null, projectName: projectName ?? null, ...rest },
  });
}

export function logActivitySearch(
  module: string,
  query: string,
  projectId?: number,
  projectName?: string | null
): void {
  const q = query.trim();
  if (!q) return;
  const key = `${module}:${q}:${projectId ?? 'na'}`;
  if (!debounceKey(key, searchStore, 600)) return;

  const projectLabel = formatActivityProjectLabel({ projectId, projectName });

  logClientAuditEvent({
    eventType: 'ACTIVITY_SEARCH',
    module: ACTIVITY_MODULE,
    entityType: 'UI',
    entityId: module,
    summary: `Searched ${module}: "${q}"${projectLabel}.`,
    displayTitle: module,
    projectId,
    projectName: projectName?.trim() || undefined,
    actionDetails: {
      module,
      query: q,
      projectId: projectId ?? null,
      projectName: projectName?.trim() || null,
    },
  });
}

export function logActivityFilterApplied(
  module: string,
  filters: Record<string, unknown>,
  projectId?: number,
  projectName?: string | null
): void {
  const key = `${module}:${JSON.stringify(filters)}:${projectId ?? 'na'}`;
  if (!debounceKey(key, filterStore, 600)) return;

  const projectLabel = formatActivityProjectLabel({ projectId, projectName });

  logClientAuditEvent({
    eventType: 'ACTIVITY_FILTER_APPLIED',
    module: ACTIVITY_MODULE,
    entityType: 'UI',
    entityId: module,
    summary: `Applied filters on ${module}${projectLabel}.`,
    displayTitle: module,
    projectId,
    projectName: projectName?.trim() || undefined,
    actionDetails: {
      module,
      filters,
      projectId: projectId ?? null,
      projectName: projectName?.trim() || null,
    },
  });
}

export function logActivityFilterCleared(
  module: string,
  projectId?: number,
  projectName?: string | null
): void {
  const projectLabel = formatActivityProjectLabel({ projectId, projectName });

  logClientAuditEvent({
    eventType: 'ACTIVITY_FILTER_CLEARED',
    module: ACTIVITY_MODULE,
    entityType: 'UI',
    entityId: module,
    summary: `Cleared filters on ${module}${projectLabel}.`,
    displayTitle: module,
    projectId,
    projectName: projectName?.trim() || undefined,
    actionDetails: {
      module,
      projectId: projectId ?? null,
      projectName: projectName?.trim() || null,
    },
  });
}

export function logActivityExport(type: string, meta?: Record<string, unknown>): void {
  const { projectId, projectName, meta: rest } = resolveActivityProjectContext(meta);
  const projectLabel = formatActivityProjectLabel({ projectId, projectName });

  logClientAuditEvent({
    eventType: 'ACTIVITY_EXPORT',
    module: ACTIVITY_MODULE,
    entityType: 'UI',
    entityId: type,
    summary: `Exported ${type}${projectLabel}.`,
    displayTitle: type,
    projectId,
    projectName,
    actionDetails: { type, projectId: projectId ?? null, projectName: projectName ?? null, ...rest },
  });
}
